/**
 * Contact Map block — interactive Leaflet map + Gravity Forms office sync.
 *
 * Round 13 (2026-08-12, item L): removed the offices `<select>` dropdown
 * that used to sit above the form (`initOfficesSelect()` + the marker↔
 * dropdown highlight wiring inside `initSingle()`, both gone) — it
 * duplicated the Gravity Forms "Predstavništvo" field already inside the
 * form (`input_2_11`), which the operator explicitly asked to keep as-is.
 * Marker click still pre-fills that GF field (`selectOfficeInForm`, kept
 * unchanged below) whenever the block's own Leaflet map is enabled — it's
 * currently off (ACF `show_map` = false), so none of the code below runs
 * today; kept dormant for whenever `show_map` is switched back on.
 */

const PIN_SIZE = 32;

function buildPin(L, code) {
	const html = `<div class="contact-map__pin"><span>${code || ''}</span></div>`;
	return L.divIcon({
		html,
		className: 'contact-map__pin-wrap',
		iconSize: [PIN_SIZE, PIN_SIZE],
		iconAnchor: [PIN_SIZE / 2, PIN_SIZE],
		popupAnchor: [0, -PIN_SIZE],
	});
}

function popupHtml(office) {
	const lines = [];

	if (office.country) {
		lines.push(`<strong>${office.country}</strong>`);
	}
	if (office.company) {
		lines.push(office.company);
	}
	if (office.address || office.city) {
		const addr = [office.city, office.address].filter(Boolean).join(', ');
		lines.push(addr);
	}
	if (office.email) {
		lines.push(`<a href="mailto:${office.email}">${office.email}</a>`);
	}
	if (office.phone) {
		const cleanPhone = office.phone.replace(/[\s()-]/g, '');
		lines.push(`<a href="tel:${cleanPhone}">${office.phone}</a>`);
	}

	return lines.join('<br>');
}

function initSingle(root) {
	if (!window.L) {
		return;
	}

	const offices = JSON.parse(root.dataset.offices || '[]');
	if (!offices.length) {
		return;
	}

	const zoom = parseInt(root.dataset.zoom, 10) || 6;
	const centerLat = parseFloat(root.dataset.centerLat) || 44.8;
	const centerLng = parseFloat(root.dataset.centerLng) || 17.3;

	const map = window.L.map(root, {
		center: [centerLat, centerLng],
		zoom,
		scrollWheelZoom: false,
		zoomControl: true,
	});

	// Carto "Positron" — minimal light basemap that matches the BEPRO B&W aesthetic.
	// Free, no API key, retina-ready.
	window.L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
		attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
		subdomains: 'abcd',
		maxZoom: 20,
	}).addTo(map);

	// Build and store one marker per office, indexed by code (lowercased).
	const markers = {};
	const bounds = [];

	offices.forEach((office) => {
		const lat = parseFloat(office.lat);
		const lng = parseFloat(office.lng);

		if (Number.isNaN(lat) || Number.isNaN(lng)) {
			return;
		}

		const marker = window.L.marker([lat, lng], {
			icon: buildPin(window.L, office.code),
			title: office.country || '',
		}).addTo(map);

		marker.bindPopup(popupHtml(office));

		const code = (office.code || '').toLowerCase();
		if (code) {
			markers[code] = marker;
		}

		bounds.push([lat, lng]);
	});

	// Fit map to show every office (with a bit of padding).
	if (bounds.length > 1) {
		map.fitBounds(bounds, { padding: [40, 40], maxZoom: 8 });
	}

	// Re-enable scroll zoom only after the user clicks the map (avoids hijacking page scroll).
	map.on('click focus', () => {
		map.scrollWheelZoom.enable();
	});
	map.on('mouseout', () => {
		map.scrollWheelZoom.disable();
	});

	const block = root.closest('.contact-map');
	if (!block) {
		return;
	}

	// Build code → office lookup so a marker click can pre-fill the Gravity
	// Form's own "Predstavništvo" field with the matching office.
	const officeByCode = {};
	offices.forEach((o) => {
		const k = (o.code || '').toLowerCase();
		if (k) {
			officeByCode[k] = o;
		}
	});

	// Find the first <select> inside the form that looks like the
	// "Predstavništvo / Office" picker, and update its value to the option
	// that matches the office's country (or city as a fallback).
	const selectOfficeInForm = (office) => {
		if (!office) {
			return;
		}

		const form = block.querySelector('.contact-map__form');
		if (!form) {
			return;
		}

		// Pick selects whose label or own option text mention "predstav" / country names.
		const selects = form.querySelectorAll('select');
		let target = null;

		selects.forEach((sel) => {
			if (target) {
				return;
			}
			const labelEl = form.querySelector(`label[for="${sel.id}"]`);
			const labelText = (labelEl ? labelEl.textContent : '').toLowerCase();
			if (labelText.includes('predstav') || labelText.includes('office')) {
				target = sel;
			}
		});

		// Fallback: any select that has at least one option containing "Hrvatska" or "Bosna".
		if (!target) {
			selects.forEach((sel) => {
				if (target) {
					return;
				}
				const hasCountry = Array.from(sel.options).some((opt) =>
					/bosna|hrvatska|srbija|slovenija/i.test(opt.text)
				);
				if (hasCountry) {
					target = sel;
				}
			});
		}

		if (!target) {
			return;
		}

		const needles = [office.country, office.city]
			.filter(Boolean)
			.map((s) => s.toLowerCase());

		let match = null;
		Array.from(target.options).forEach((opt) => {
			if (match) {
				return;
			}
			const haystack = `${opt.text} ${opt.value}`.toLowerCase();
			if (needles.some((n) => n && haystack.includes(n))) {
				match = opt;
			}
		});

		if (!match || target.value === match.value) {
			return;
		}

		target.value = match.value;

		// Native change event covers vanilla listeners + Gravity Forms conditional logic.
		target.dispatchEvent(new Event('change', { bubbles: true }));

		// Gravity Forms' Enhanced UI uses Chosen — needs a jQuery trigger to repaint.
		if (window.jQuery) {
			window.jQuery(target).trigger('change').trigger('chosen:updated');
		}
	};

	// Marker click → pan/zoom to it + pre-fill the Gravity Form's own
	// "Predstavništvo" field with the matching office.
	const focusOffice = (code) => {
		const key = (code || '').toLowerCase();
		const marker = markers[key];

		if (marker) {
			map.setView(marker.getLatLng(), Math.max(map.getZoom(), 7), { animate: true });
			marker.openPopup();
		}

		selectOfficeInForm(officeByCode[key]);
	};

	Object.entries(markers).forEach(([code, marker]) => {
		marker.on('click', () => focusOffice(code));
	});
}

function waitForLeaflet(callback, attempts = 40) {
	if (window.L) {
		callback();
		return;
	}
	if (attempts <= 0) {
		return;
	}
	setTimeout(() => waitForLeaflet(callback, attempts - 1), 100);
}

export default function contactMap() {
	// Leaflet map + marker→form sync: only when the block's own mini map is
	// present (ACF `show_map`, currently off).
	const mapRoots = document.querySelectorAll('[data-contact-map]');
	if (!mapRoots.length) {
		return;
	}

	waitForLeaflet(() => {
		mapRoots.forEach((root) => initSingle(root));
	});
}
